<template>
  <form @submit.prevent="handleSubmit" class="space-y-6">

    <!-- Champ Email -->
    <div class="space-y-2">
      <label for="username" class="flex items-center gap-2 text-sm font-semibold text-gray-700">
        <svg class="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207" />
        </svg>
        Adresse Email
      </label>

      <div class="relative group">
        <input
          id="username"
          v-model="username"
          type="email"
          class="w-full px-4 py-3.5 text-sm bg-gray-50 border-2 border-gray-200 rounded-xl focus:bg-white focus:ring-4 focus:ring-blue-500/20 focus:border-blue-500 transition-all duration-200 placeholder-gray-400"
          :class="localErrors.username ? 'border-red-400 bg-red-50 focus:border-red-500 focus:ring-red-500/20' : 'group-hover:border-gray-300'"
          placeholder="votre.email@exemple.com"
          required
        />

        <!-- Icône de validation -->
        <div
          v-if="username && !localErrors.username"
          class="absolute right-3 top-1/2 transform -translate-y-1/2"
        >
          <svg class="w-5 h-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
          </svg>
        </div>
      </div>

      <div v-if="localErrors.username" class="flex items-center gap-2 text-red-500 text-sm">
        <svg class="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
          <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
        </svg>
        {{ localErrors.username }}
      </div>
    </div>

    <!-- Champ Mot de passe -->
    <div class="space-y-2">
      <label for="password" class="flex items-center gap-2 text-sm font-semibold text-gray-700">
        <svg class="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
        </svg>
        Mot de passe
      </label>

      <div class="relative group">
        <input
          id="password"
          v-model="password"
          :type="showPassword ? 'text' : 'password'"
          class="w-full px-4 py-3.5 pr-12 text-sm bg-gray-50 border-2 border-gray-200 rounded-xl focus:bg-white focus:ring-4 focus:ring-blue-500/20 focus:border-blue-500 transition-all duration-200 placeholder-gray-400"
          :class="localErrors.password ? 'border-red-400 bg-red-50 focus:border-red-500 focus:ring-red-500/20' : 'group-hover:border-gray-300'"
          placeholder="••••••••"
          required
        />

        <!-- Bouton toggle password -->
        <button
          type="button"
          @click="togglePasswordVisibility"
          class="absolute cursor-pointer right-3 top-1/2 transform -translate-y-1/2 p-1 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
        >
          <svg v-if="showPassword" class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
          </svg>
          <svg v-else class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
          </svg>
        </button>
      </div>

      <div v-if="localErrors.password" class="flex items-center gap-2 text-red-500 text-sm">
        <svg class="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
          <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
        </svg>
        {{ localErrors.password }}
      </div>
    </div>

    <!-- Checkbox Se souvenir -->
    <div class="flex items-center justify-between gap-3">
      <div class="flex items-center gap-3">
        <div class="relative">
        <input
          v-model="rememberMe"
          type="checkbox"
          id="remember"
          class="peer sr-only"
        />
        <label
          for="remember"
          class="flex items-center justify-center w-5 h-5 border-2 border-gray-300 rounded-md cursor-pointer transition-all duration-200 hover:border-blue-400 peer-checked:bg-blue-500 peer-checked:border-blue-500 peer-focus:ring-4 peer-focus:ring-blue-500/20"
        >
          <svg
            class="w-3 h-3 text-white opacity-0 peer-checked:opacity-100 transition-opacity duration-200"
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
          </svg>
        </label>
      </div>
      <label for="remember" class="text-sm text-gray-600 cursor-pointer select-none">
        Se souvenir de moi
      </label>
      </div>
      <a
        href="#"
        @click.prevent="$emit('forgot-password-requested')"
        class="group inline-flex cursor-pointer items-center gap-2 text-sm font-medium text-blue-600 hover:text-blue-700 transition-all duration-200 hover:gap-3"
      >
        <svg class="w-4 h-4 transition-transform duration-200 group-hover:scale-110" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        Mot de passe oublié ?
      </a>

    </div>

    <button
      type="submit"
      class="group relative cursor-pointer w-full bg-gradient-to-r from-primary-300 to-primary-600 hover:from-primary-500 hover:to-primary-700 text-white font-semibold py-3.5 px-6 rounded-xl transition-all duration-200 shadow-lg shadow-blue-500/25 hover:shadow-xl hover:shadow-blue-500/30 disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:shadow-lg focus:outline-none focus:ring-4 focus:ring-blue-500/20"
      :disabled="isLoading"
    >
      <div class="flex items-center justify-center gap-3">
        <div v-if="isLoading" class="w-5 h-5">
          <svg class="animate-spin w-5 h-5" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
        </div>

        <svg v-else class="w-5 h-5 transition-transform duration-200 group-hover:scale-110" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" />
        </svg>

        <span>{{ isLoading ? 'Connexion en cours...' : 'Se connecter' }}</span>
      </div>

      <!-- Effet de brillance au hover -->
      <div class="absolute inset-0 rounded-xl bg-gradient-to-r from-white/0 via-white/10 to-white/0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 group-hover:animate-pulse"></div>
    </button>

    <!-- Lien mot de passe oublié -->
    <div class="text-center pt-2">

    </div>
  </form>
</template>

<script setup>
import { ref, reactive } from 'vue'

const props = defineProps({
  isLoading: Boolean,
  showPassword: Boolean,
  togglePasswordVisibility: Function,
})

const emit = defineEmits(['login-submit', 'forgot-password-requested'])

const username = ref('')
const password = ref('')
const rememberMe = ref(false)

const localErrors = reactive({})

const validateForm = () => {
  localErrors.username = ''
  localErrors.password = ''

  if (!username.value.trim()) {
    localErrors.username = "L'adresse email est requise"
  } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(username.value)) {
    // localErrors.username = 'L\'adresse email n\'est pas valide';
  }

  if (!password.value) {
    localErrors.password = 'Le mot de passe est requis'
  }

  return Object.keys(localErrors).every((key) => !localErrors[key])
}

const handleSubmit = () => {
  if (validateForm()) {
    emit('login-submit', {
      username: username.value,
      password: password.value,
      rememberMe: rememberMe.value,
    })
  }
}
</script>

<style scoped></style>
